/* lead-time-line.js
 *
 * Upgrades the server-rendered "Ships to store · Get it <date>" text on cart
 * line items (snippets/lead-time-line.liquid) to "Available for pickup today"
 * when the customer's selected store has the variant in stock — mirroring
 * the product-page pickup widget so cart and product page agree.
 *
 * Source of truth: /apps/roo/stock-locations (HMAC-signed via Shopify App
 * Proxy, served by oms/public/routes/stock-locations.js). Selected store is
 * read from localStorage('selected-store'), same key the product widget uses.
 *
 * Re-runs on:
 *   - DOMContentLoaded (initial cart page)
 *   - 'storechanged' (user switches stores)
 *   - MutationObserver on body (mini-cart drawer opens / cart re-renders)
 */
(function(){
  if (typeof window === 'undefined') return;

  const PICKUP_TODAY_TEXT = 'Available for pickup today';
  const SELECT_STORE_PROMPT = 'Select your store to check availability';

  function getSelectedStoreHandle(){
    try { return localStorage.getItem('selected-store') || null; } catch (_) { return null; }
  }

  // Render the "Select your store" prompt — a button that opens the same
  // modal-store-selector the pickup widget uses. We replace the text-only
  // child so the prompt is keyboard-accessible (button, not bare onclick).
  function showSelectStorePrompt(el){
    if (!el) return;
    const textEl = el.querySelector('.lead-time-line__text');
    if (!textEl) return;
    textEl.textContent = '';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lead-time-line__select-store';
    btn.textContent = SELECT_STORE_PROMPT + ' →';
    btn.addEventListener('click', function(e){
      e.preventDefault();
      const modal = document.getElementById('modal-store-selector');
      if (modal && typeof modal.show === 'function') modal.show();
    });
    textEl.appendChild(btn);
    el.setAttribute('data-hydrated', 'prompt');
  }

  function applyUpgrade(el, leadTime){
    if (!el) return;
    const textEl = el.querySelector('.lead-time-line__text');
    if (!textEl) return;
    // No lead-time entry for the selected store → that store cannot fulfill
    // this item. Leave the server-rendered text untouched.
    if (!leadTime) return;
    if (leadTime.inStock === true) {
      textEl.textContent = PICKUP_TODAY_TEXT;
    } else if (leadTime.readyLabel) {
      // Backend-computed "Ships to store — get it by <date>" line.
      textEl.textContent = leadTime.readyLabel;
    } else {
      return;
    }
    el.setAttribute('data-hydrated', 'true');
  }

  let _inFlight = null;
  function hydrate(){
    const els = Array.from(document.querySelectorAll('[data-lead-time-line]'));
    if (!els.length) return;

    const selectedStore = getSelectedStoreHandle();
    // No selected store → no way to know per-store availability. Prompt the
    // customer to pick one so we can show accurate pickup info.
    if (!selectedStore) { els.forEach(showSelectStorePrompt); return; }

    const variantIds = Array.from(new Set(els.map(e => e.getAttribute('data-variant-id')).filter(Boolean)));
    if (!variantIds.length) return;

    // De-dupe in-flight fetches so rapid re-runs (mini-cart open, store change)
    // share one network round-trip.
    const key = `${selectedStore}|${variantIds.sort().join(',')}`;
    if (_inFlight && _inFlight.key === key) { _inFlight.promise.then(apply); return; }

    const promise = fetch(`/apps/roo/stock-locations?variant_ids=${encodeURIComponent(variantIds.join(','))}`, {
      credentials: 'same-origin',
    })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);

    _inFlight = { key, promise };

    promise.then(apply).finally(() => { if (_inFlight && _inFlight.key === key) _inFlight = null; });

    function apply(data){
      if (!data || !data.variants) return;
      els.forEach(el => {
        const vid = el.getAttribute('data-variant-id');
        const leadTimes = (data.variants[vid] && data.variants[vid].leadTimes) || {};
        applyUpgrade(el, leadTimes[selectedStore] || null);
      });
    }
  }

  function schedule(){
    // Debounce so mini-cart open + observer + storechanged don't fire 3 fetches.
    clearTimeout(schedule._t);
    schedule._t = setTimeout(hydrate, 50);
  }

  document.addEventListener('DOMContentLoaded', schedule);
  // Some themes fire pickup-style events on the store-selector when the
  // customer changes stores; re-hydrate so "today" updates accordingly.
  document.addEventListener('storechanged', schedule, true);
  document.addEventListener('storelocationsearched', schedule, true);

  // Mini-cart drawer is added/swapped dynamically — watch for new lead-time
  // elements appearing in the DOM and re-hydrate.
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(muts => {
      for (const m of muts) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-lead-time-line]')) { schedule(); return; }
          if (node.querySelector && node.querySelector('[data-lead-time-line]')) { schedule(); return; }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // If the script loads after DOMContentLoaded (deferred / async), still run.
  if (document.readyState === 'interactive' || document.readyState === 'complete') schedule();
})();
